import { Col, Row } from "antd"
import React, { useEffect, useState } from "react"

import { Button } from "components/Button"
import { UiArrow } from "components/Carousel/Ui"
import { cdn } from "core/cdn"

import styles from "./Call.module.scss"
import { DEVICE_STATE } from "./contexts/videoContext"
import { UNKNOWN_DEVICE } from "./types/types"
import {
  CANNOT_TURN_OFF,
  DO_NOT_NEED_TUTO,
  TUTORIAL_STEPS,
  TUTO_STEP,
  TutorialState,
  WARNING_MEDIA_DEVICE_TIMER,
  deviceName,
  tutorialState,
} from "./tutorialSteps"

import DOMPurify from "dompurify";
import { useLanguage } from "locales"
import { IStrings } from "locales/languages"
import Loader from "components/Loaders/Loader/Loader"

interface Device {
  label: string
  state: DEVICE_STATE
}
interface StepProps {
  finish?: () => void
  next?: () => void
  previous?: () => void
  device: Device
  deviceWarning?: boolean
}
enum FOLLOW_INSTRUCTIONS_STEP {
  TAKE = 1,
  FOLLOW_INSTRUCTIONS = 2,
  PUT_DOWN = 3,
}

const TIMER_PER_STEP_IN_SEC = 20

const stepImage = (tutoStep: TUTO_STEP, label: string) =>
  label && {
    src: TUTORIAL_STEPS[tutoStep].images(label)[0],
    alt: deviceName(label),
  }

const stepHtml = (tutoStep: TUTO_STEP, languages: IStrings, label: string) => ({
  __html: DOMPurify.sanitize(TUTORIAL_STEPS[tutoStep].text(languages, label), {
    USE_PROFILES: { html: true },
  }),
})

const Timer = (props: { timer: number }): JSX.Element => {
  return (
    <div className={styles.Timer}>
      <img src={cdn("images/devices/tutorial_device/waiting.svg")} alt="" />
      <span>{props.timer}</span>
    </div>
  )
}
const NavigationStep = (props: {
  next?: () => void
  previous?: () => void
  img?: { src: string; alt: string }
  children?: JSX.Element
}) => {
  return (
    <Row gutter={[20, 20]} align="middle">
      <Col style={{ width: "100px" }}>
        <UiArrow
          side="left"
          onClick={() => props.previous && props.previous()}
          hidden={!props.previous}
        />
      </Col>
      <Col>
        {props.img && (
          <img
            className={styles.ImageTuto}
            src={props.img.src}
            alt={props.img.alt}
          />
        )}
        {props.children}
      </Col>
      <Col style={{ width: "100px" }}>
        <UiArrow
          side="right"
          onClick={() => props.next && props.next()}
          hidden={!props.next}
        />
      </Col>
    </Row>
  )
}

const TakeStep = (props: StepProps) => {
  const { languages } = useLanguage()
  return (
    <>
      <NavigationStep img={stepImage(TUTO_STEP.TAKE, props.device.label)} />
      <span
        className={styles.TitleTuto}
        dangerouslySetInnerHTML={stepHtml(TUTO_STEP.TAKE, languages, props.device.label)}
      />
      <Button type="primary" wide="long" onClick={props.next}>
        {languages.next}
      </Button>
    </>
  )
}

const TurnOnStep = (props: StepProps) => {
  const { languages } = useLanguage()
  return (
    <>
      <NavigationStep
        previous={props.previous}
        img={stepImage(TUTO_STEP.TURN_ON, props.device.label)}
      />
      <Loader style={{ margin: "0 auto" }} size="30px" />
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }} dangerouslySetInnerHTML={{
        __html: DOMPurify.sanitize(languages.deviceWaitingMessage, { USE_PROFILES: { html: true } }),
      }}>
      </div>
      <span
        className={`${styles.TitleTuto} uppercase-first-letter`}
        dangerouslySetInnerHTML={stepHtml(TUTO_STEP.TURN_ON, languages, props.device.label)}
      />
      {props.deviceWarning && (
        <span className={styles.WarningDevice}>
          <img
            style={{ height: "45px" }}
            src={cdn("images/devices/tutorial_device/attention.png")}
            alt="attention_icon_picture"
          />
          {languages.seemsDeviceNotOn(deviceName(props.device.label, "short"))}
        </span>
      )}
    </>
  )
}

const LightStep = (props: StepProps) => {
  const { languages } = useLanguage()
  const [timer, setTimer] = useState<number>(TIMER_PER_STEP_IN_SEC)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const interval = setInterval(() => {
      if (timer === 0) {
        props.next && props.next()
        return () => clearInterval(interval)
      }
      setTimer((timer) => timer - 1)
    }, 1000)
    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timer])

  return (
    <>
      <NavigationStep
        next={props.next}
        img={stepImage(TUTO_STEP.LIGHT, props.device.label)}
      />
      <span
        className={`${styles.TitleTuto} uppercase-first-letter`}
        dangerouslySetInnerHTML={stepHtml(TUTO_STEP.LIGHT, languages, props.device.label)}
      />
      <Timer timer={timer} />
    </>
  )
}

const UnpluggedStep = (props: StepProps) => {
  const { languages } = useLanguage()
  return (
    <>
      <NavigationStep img={stepImage(TUTO_STEP.UNPLUGGED, props.device.label)} />
      <span
        className={styles.TitleTuto}
        dangerouslySetInnerHTML={stepHtml(TUTO_STEP.UNPLUGGED, languages, props.device.label)}
      />
    </>
  )
}

const PluggedStep = (props: StepProps) => {
  const { languages } = useLanguage()
  return (
    <>
      <NavigationStep img={stepImage(TUTO_STEP.PLUGGED, props.device.label)} />

      <span
        className={styles.TitleTuto}
        dangerouslySetInnerHTML={stepHtml(TUTO_STEP.PLUGGED, languages, props.device.label)}
      />
    </>
  )
}

const FocusStep = (props: StepProps) => {
  const { languages } = useLanguage()
  return (
    <>
      <NavigationStep
        previous={props.previous}
        img={stepImage(TUTO_STEP.FOCUS, props.device.label)}
      />

      <span
        className={styles.TitleTuto}
        dangerouslySetInnerHTML={stepHtml(TUTO_STEP.FOCUS, languages, props.device.label)}
      />
    </>
  )
}
const PlacingStep = (props: StepProps) => {
  const { languages } = useLanguage()
  const [timer, setTimer] = useState<number>(TIMER_PER_STEP_IN_SEC)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const interval = setInterval(() => {
      if (timer === 0) {
        props.next && props.next()
        return () => clearInterval(interval)
      }
      setTimer((timer) => timer - 1)
    }, 1000)
    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timer])

  return (
    <>
      <NavigationStep
        previous={props.previous}
        next={props.next}
        img={stepImage(TUTO_STEP.PLACING, props.device.label)}
      />

      <span
        className={`${styles.TitleTuto} uppercase-first-letter`}
        dangerouslySetInnerHTML={stepHtml(TUTO_STEP.PLACING, languages, props.device.label)}
      ></span>
      <Timer timer={timer} />
    </>
  )
}
const TurnOffStep = (props: StepProps) => {
  // Pour les outils ne s'éteignant pas ( le stéthoscope ),
  // on demande de le reposer
  const { languages } = useLanguage()
  if (CANNOT_TURN_OFF.includes(props.device.label)) {
    return <PutDownStep {...props} next={props.finish} />
  }

  return (
    <>
      <NavigationStep img={stepImage(TUTO_STEP.TURN_OFF, props.device.label)} />

      <span
        className={`${styles.TitleTuto} uppercase-first-letter`}
        dangerouslySetInnerHTML={stepHtml(TUTO_STEP.TURN_OFF, languages, props.device.label)}
      ></span>
      <Button type="primary" wide="long" onClick={props.finish}>
        {languages.iTurnOff}
      </Button>
    </>
  )
}
const header = (tutoStep: TUTO_STEP) => TUTORIAL_STEPS[tutoStep]?.header ?? ""


const isUnknownDevice = (label?: string): boolean => !label || label === "" || label === UNKNOWN_DEVICE

const TutoStepper = ({
  tutoStep,
  ...props
}: {
  finish: () => void
  previous: () => void
  next: () => void
  device: Device
  deviceWarning: boolean
  tutoStep: TUTO_STEP
}): JSX.Element => {
  switch (tutoStep) {
    case TUTO_STEP.UNPLUGGED:
      return <UnpluggedStep {...props} />
    case TUTO_STEP.PLUGGED:
      return <PluggedStep {...props} />
    case TUTO_STEP.TAKE:
      return <TakeStep {...props} />
    case TUTO_STEP.TURN_ON:
      return <TurnOnStep {...props} />
    case TUTO_STEP.LIGHT:
      return <LightStep {...props} />
    case TUTO_STEP.FOCUS:
      return <FocusStep {...props} />
    case TUTO_STEP.PLACING:
      return <PlacingStep {...props} />
    case TUTO_STEP.TURN_OFF:
      return <TurnOffStep {...props} />
    default:
      return <></>
  }
}

const FollowInstructionsStep = (props: StepProps) => {
  const { languages } = useLanguage()
  return (
    <>
      <NavigationStep
        img={stepImage(TUTO_STEP.FOLLOW_INSTRUCTIONS, props.device.label)}
      />

      <span
        className={`${styles.TitleTuto} uppercase-first-letter`}
        dangerouslySetInnerHTML={stepHtml(
          TUTO_STEP.FOLLOW_INSTRUCTIONS,
          languages,
          props.device.label,
        )}
      />
    </>
  )
}

const PutDownStep = (props: StepProps) => {
  const { languages } = useLanguage()
  return (
    <>
      <NavigationStep img={stepImage(TUTO_STEP.TURN_OFF, props.device.label)} />
      <span
        className={`${styles.TitleTuto} uppercase-first-letter`}
        dangerouslySetInnerHTML={stepHtml(TUTO_STEP.TURN_OFF, languages, props.device.label)}
      />
      <Button type="primary" wide="long" onClick={props.next}>
        {languages.finish}
      </Button>
    </>
  )
}

const FollowInstructionStepper = (
  props: StepProps & { onStepChange: (tutoStep: TUTO_STEP) => void },
) => {
  const [followInstructionSteps, setFollowInstructionSteps] =
    useState<FOLLOW_INSTRUCTIONS_STEP>(FOLLOW_INSTRUCTIONS_STEP.TAKE)

  const { onStepChange } = props
  useEffect(() => {
    onStepChange(
      followInstructionSteps === FOLLOW_INSTRUCTIONS_STEP.TAKE
        ? TUTO_STEP.TAKE
        : TUTO_STEP.FOLLOW_INSTRUCTIONS,
    )
  }, [followInstructionSteps, onStepChange])

  switch (followInstructionSteps) {
    case FOLLOW_INSTRUCTIONS_STEP.TAKE:
      return (
        <TakeStep
          device={props.device}
          next={() => {
            setFollowInstructionSteps(
              FOLLOW_INSTRUCTIONS_STEP.FOLLOW_INSTRUCTIONS
            )
          }}
        />
      )
    case FOLLOW_INSTRUCTIONS_STEP.FOLLOW_INSTRUCTIONS:
      return (
        <FollowInstructionsStep
          device={props.device}
          previous={() => {
            setFollowInstructionSteps(FOLLOW_INSTRUCTIONS_STEP.TAKE)
          }}
        />
      )
    default:
      return <></>
  }
}

export const DeviceManager = (props: {
  device?: Device
  onStop: () => void
  onTutorialState?: (state: TutorialState | null) => void
}) => {
  const [tutoStep, setTutoStep] = useState<TUTO_STEP>(TUTO_STEP.TAKE)
  // Step actually on screen: FollowInstructionStepper runs its own sequence.
  const [displayedStep, setDisplayedStep] = useState<TUTO_STEP>(TUTO_STEP.TAKE)
  // Lifted out of TurnOnStep so the doctor gets the warning the patient sees.
  const [deviceWarning, setDeviceWarning] = useState<boolean>(false)
  const { currentLang } = useLanguage()
  const { onTutorialState } = props

  useEffect(() => {
    // Device State
    const currentDevice = props.device
    if (!currentDevice) {
      setTutoStep(TUTO_STEP.TAKE)
      return
    }

    if (isUnknownDevice(currentDevice.label)) {
      props.onStop()
      return
    }

    switch (currentDevice.state) {
      case DEVICE_STATE.UNPLUGGED:
        setTutoStep(TUTO_STEP.UNPLUGGED)
        break
      case DEVICE_STATE.WAITING:
        setTutoStep(TUTO_STEP.TAKE)
        break
      case DEVICE_STATE.USING:
        if (DO_NOT_NEED_TUTO.includes(currentDevice.label))
          setTutoStep(TUTO_STEP.FOLLOW_INSTRUCTIONS)
        else setTutoStep(TUTO_STEP.LIGHT)
        break
      case DEVICE_STATE.CLOSING:
        setTutoStep(TUTO_STEP.TURN_OFF)
        break
    }
  }, [props.device])

  useEffect(() => {
    // FollowInstructionStepper reports its own step, and its effect runs before this one: syncing here would overwrite it.
    if (tutoStep !== TUTO_STEP.FOLLOW_INSTRUCTIONS) setDisplayedStep(tutoStep)
  }, [tutoStep])

  useEffect(() => {
    setDeviceWarning(false)
    if (displayedStep !== TUTO_STEP.TURN_ON) return
    const timeout = setTimeout(
      () => setDeviceWarning(true),
      WARNING_MEDIA_DEVICE_TIMER * 1000,
    )
    return () => clearTimeout(timeout)
  }, [displayedStep])

  useEffect(() => {
    if (!onTutorialState) return
    const label = props.device?.label
    onTutorialState(
      label
        ? tutorialState(displayedStep, label, currentLang, deviceWarning)
        : null,
    )
  }, [displayedStep, props.device, currentLang, deviceWarning, onTutorialState])

  return (
    <div
      className={`${styles.DeviceContainer} ${props.device ? styles.Active : ""
        }`}
    >
      <div className={styles.HeaderDevice}>{header(displayedStep)}</div>
      {props.device && !isUnknownDevice(props.device.label) &&
        (tutoStep !== TUTO_STEP.FOLLOW_INSTRUCTIONS ? (
          <TutoStepper
            previous={() => setTutoStep(tutoStep - 1)}
            next={() => setTutoStep(tutoStep + 1)}
            tutoStep={tutoStep}
            finish={props.onStop}
            device={props.device}
            deviceWarning={deviceWarning}
          />
        ) : (
          <FollowInstructionStepper
            device={props.device}
            onStepChange={setDisplayedStep}
          />
        ))}
    </div>
  )
}
